import { scrubStr } from '@atlaskit/adf-utils/scrub';
import {
	ACTION,
	ACTION_SUBJECT,
	EVENT_TYPE,
	ACTION_SUBJECT_ID,
} from '@atlaskit/editor-common/analytics';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';

import type { API } from '../../aiStreamingOrchestratorPluginType';

export function findNodeByLocalIdAndDoc(
	doc: Node,
	localId: string,
): { node: Node; parent: Node | null; pos: number } | undefined {
	let nodeResult: { node: Node; parent: Node | null; pos: number } | undefined;
	doc.descendants((node, pos, parent) => {
		if (nodeResult) {
			return false;
		}
		if (node.attrs.localId === localId) {
			nodeResult = { node, pos, parent };
		}
		return !nodeResult;
	});
	return nodeResult;
}

/**
 * Recursively walks a ProseMirror document and returns the set of every
 * node's `localId` attribute at every depth.
 *
 * Used by `StreamCommandQueue`'s apply-anomaly diagnostics
 * (`unexpected_node_removal`) to take pre-apply and post-apply snapshots
 * so the diff catches removals at any depth — including inside layouts,
 * expand blocks, panels, and table cells. The previous top-level-only
 * variant (`snapshotTopLevelLocalIds`) produced false positives for
 * commands that nested a previously-top-level node (e.g. `wrap` /
 * `moveNode`) and false negatives for collateral removals deep in the
 * tree.
 *
 * Uses the same `descendants` walk semantics as `findNodeByLocalIdAndDoc`
 * (the lookup helper every targeted command uses), so the orchestrator's
 * detection logic and the commands' apply paths share a single
 * tree-walking model.
 */
export function collectAllLocalIds(doc: Node | undefined): Set<string> {
	const ids = new Set<string>();
	if (!doc) {
		return ids;
	}
	doc.descendants((node) => {
		const localId = node.attrs?.localId;
		if (typeof localId === 'string' && localId.length > 0) {
			ids.add(localId);
		}
		return true;
	});
	return ids;
}

/**
 * This find will search through the entire document and only return the last breadth-first node match rather then the first.
 * This is NOT a depth-first search.
 * @param doc
 * @param localId
 * @returns
 */
export function findLastNodeByLocalIdAndDoc(
	doc: Node,
	localId: string,
): { node: Node; parent: Node | null; pos: number } | undefined {
	let nodeResult: { node: Node; parent: Node | null; pos: number } | undefined;
	doc.descendants((node, pos, parent) => {
		if (node.attrs.localId === localId) {
			nodeResult = { node, pos, parent };
			return false;
		}
		return true;
	});
	return nodeResult;
}

export function findNodeByLocalIdAndApi(
	api: API | undefined,
	localId: string | false,
): Node | undefined {
	if (!localId) {
		return undefined;
	}
	let foundNode: Node | undefined;
	api?.core.actions.execute(({ tr }) => {
		foundNode = findNodeByLocalIdAndDoc(tr.doc, localId)?.node;
		return null;
	});
	return foundNode;
}

/**
 * Finds a node by its localId and calls the callback with the node, position, and parent.
 * @param tr - The transform to search in.
 * @param localId - The localId of the node to find.
 * @param callback - The callback to call with the node, position, and parent.
 * @returns The transform.
 */
export function findNodeByLocalId(
	api: API | undefined,
	tr: Transaction,
	localId: string,
	callback: ({ node, pos, parent }: { node: Node; parent: Node | null; pos: number }) => void,
	reversed?: boolean,
): Transaction | null {
	const result = (reversed ? findLastNodeByLocalIdAndDoc : findNodeByLocalIdAndDoc)(
		tr.doc,
		localId,
	);

	if (result) {
		api?.aiExperience?.actions.tagAsAIStreamingTransformation(tr);
		callback(result);
	}
	if (!result) {
		const localIdState = api?.localId?.sharedState.currentState();

		let localIdStatus: string;
		if (!localIdState) {
			localIdStatus = 'localIdPluginMissing';
		} else if (!localIdState.localIdWatchmenEnabled) {
			localIdStatus = 'localIdWatchmenDisabled';
		} else {
			localIdStatus = localIdState.localIdStatus?.get(localId) ?? 'notFound';
		}

		let docSize: number | undefined;
		// Quick and dirty way to get the node size - do not copy this pattern, use `requestDocument`
		api?.core.actions.execute(({ tr }) => {
			docSize = tr.doc.nodeSize;
			return null;
		});
		api?.analytics?.actions.fireAnalyticsEvent({
			action: ACTION.LOCAL_ID_NOT_FOUND,
			actionSubject: ACTION_SUBJECT.AI_STREAMING,
			actionSubjectId: ACTION_SUBJECT_ID.EXPERIENCE_APPLICATION,
			eventType: EVENT_TYPE.OPERATIONAL,
			attributes: {
				localIdLength: localId?.length,
				docSize,
				scrubbedLocalId: scrubStr(localId),
				localIdStatus: fg('platform_editor_ai_aifc_localid_error_reporting')
					? localIdStatus
					: 'unavailable',
				localIdStatusSize: localIdState?.localIdStatus?.size ?? 0,
			},
		});
		return null;
	}
	return tr;
}
